import React from "react";
import { StyleSheet } from "react-native";

import { Box } from "../../../../../../mads";
import { useViewjobProctorAllocation } from "../../../../../proctor/__generated__/ViewjobProctorContext";
import { MobVjJobSummaryTst } from "../../../../../proctor/__generated__/ViewjobProctorSpecifications";
import {
  useIsDesktopDisplay,
  useViewjobOneGraphModel,
} from "../../../../context/ViewJobDataContext";
import { OGWrappedInlineHeader } from "../InlineHeader/InlineHeader";
import { JobTitle } from "../JobTitle";
import { Logo } from "../Logo";

// These Model interfaces will be replaced by types generated from GraphQL fragments.
interface CompanyImagesModel {
  showIconInTitle?: boolean;
  showBannerTop?: boolean;
  logoUrl?: string;
  logoAltText?: string;
}

export interface CompanyInfoWithLogoProps {
  jobTitle?: string;
  isSimpleVjImproveActive?: boolean;
  companyImagesModel?: CompanyImagesModel;
}

export const CompanyInfoWithLogo: React.FunctionComponent<CompanyInfoWithLogoProps> = ({
  jobTitle,
  isSimpleVjImproveActive,
  companyImagesModel,
}: CompanyInfoWithLogoProps) => {
  const mobVjJobSummaryTstActive = useViewjobProctorAllocation(MobVjJobSummaryTst).value > 0;
  const isDesktop = useIsDesktopDisplay();
  const useBrandedDesktopSpacing = isDesktop && !!companyImagesModel?.showBannerTop;
  const jobTitleLevel = isDesktop ? 5 : mobVjJobSummaryTstActive ? 4 : undefined;

  return (
    <>
      <Box
        testID="company-info-title-row"
        sx={[
          companyImagesModel?.showIconInTitle && styles.titleWithIcon,
          isDesktop && styles.desktopTitle,
          useBrandedDesktopSpacing && styles.brandedDesktopTitle,
        ]}
      >
        <JobTitle
          jobTitle={jobTitle || ""}
          level={jobTitleLevel}
          useBrandedDesktopSpacing={useBrandedDesktopSpacing}
        />
        {companyImagesModel?.showIconInTitle && companyImagesModel.logoUrl && (
          <Logo
            url={companyImagesModel.logoUrl}
            altText={companyImagesModel.logoAltText || ""}
            size={32}
            hasShadow={false}
            hasBorder={false}
          />
        )}
      </Box>
      <Box
        testID="company-info-metadata"
        sx={{
          flexDirection: "row",
          marginBottom: useBrandedDesktopSpacing
            ? 4
            : !isDesktop && isSimpleVjImproveActive
              ? 16
              : 8,
        }}
      >
        <OGWrappedInlineHeader />
      </Box>
    </>
  );
};

type OGWrapperOmitKeys = "jobTitle" | "isSimpleVjImproveActive" | "companyImagesModel";
export interface OGWrappedCompanyInfoWithLogoProps extends Omit<
  CompanyInfoWithLogoProps,
  OGWrapperOmitKeys
> {}

export function OGWrappedCompanyInfoWithLogo({}: OGWrappedCompanyInfoWithLogoProps) {
  const ogModel = useViewjobOneGraphModel();

  if (ogModel == null) {
    return null;
  }
  const companyImagesModel = ogModel.employer?.companyImagesModel
    ? {
        showIconInTitle: ogModel.employer.companyImagesModel.showIconInTitle,
        showBannerTop: ogModel.employer.companyImagesModel.showBannerTop,
        logoUrl: ogModel.employer.companyImagesModel.logoUrl,
        logoAltText: ogModel.employer.companyImagesModel.logoAltText,
      }
    : undefined;

  return (
    <CompanyInfoWithLogo
      jobTitle={ogModel.job?.jobTitle}
      isSimpleVjImproveActive={ogModel.features?.isSimpleVjImproveActive || false}
      companyImagesModel={companyImagesModel}
    />
  );
}

const styles = StyleSheet.create({
  titleWithIcon: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 16,
  },
  desktopTitle: {
    marginTop: 16,
  },
  brandedDesktopTitle: {
    marginTop: 0,
  },
});
